import { Component, inject, input, Input, OnInit, output } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { FloorplanFlameDetectorPlaceholder } from "@domain/project/floorplan/floorplan-flame-detector-placeholder";
import { FloorplanGasWarningCenterPlaceholder } from "@domain/project/floorplan/floorplan-gas-warning-center-placeholder";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanSidesheetExtent } from "@domain/project/floorplan/floorplan-sidesheet-extent";
import { FloorplanSimplePlaceholder } from "@domain/project/floorplan/floorplan-simple-placeholder";
import { FloorplanTransmitterPlaceholder } from "@domain/project/floorplan/floorplan-transmitter-placeholder";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { FloorplanFlameDetectorDetailFormComponent } from "@project/floorplanner/floorplan-additional-notes/floorplan-flame-detector-detail-form/floorplan-flame-detector-detail-form.component";
import { FloorplanGasWarningCenterDetailFormComponent } from "@project/floorplanner/floorplan-additional-notes/floorplan-gas-warning-center-detail-form/floorplan-gas-warning-center-detail-form.component";
import { FloorplanItemDetailsComponent } from "@project/floorplanner/floorplan-additional-notes/floorplan-item-details/floorplan-item-details.component";
import { FloorplanTransmitterDetailFormComponent } from "@project/floorplanner/floorplan-additional-notes/floorplan-transmitter-detail-form/floorplan-transmitter-detail-form.component";
import { FloorplanCustomPartNumbersComponent } from "@project/floorplanner/floorplan-custom-part-numbers/floorplan-custom-part-numbers.component";
import { FloorplanItemGroupComponent } from "@project/floorplanner/floorplan-item-group/floorplan-item-group.component";
import { filter, map, Observable } from "rxjs";

@Component({
  selector: "app-placeholder-notes",
  templateUrl: "./placeholder-notes.component.html",
  imports: [
    UiKitModule,
    FloorplanItemDetailsComponent,
    ReactiveFormsModule,
    FloorplanTransmitterDetailFormComponent,
    FloorplanGasWarningCenterDetailFormComponent,
    FloorplanFlameDetectorDetailFormComponent,
    FloorplanItemGroupComponent,
    FloorplanCustomPartNumbersComponent,
  ],
  standalone: true,
})
export class PlaceholderNotesComponent implements OnInit {
  private userService = inject(UserService);
  private projectService = inject(ProjectService);

  protected imageUrl!: string;
  protected isReadOnlyUser$: Observable<boolean>;
  protected hasWritePermission$: Observable<boolean | undefined>;
  private _floorplanItem!:
    | FloorplanSimplePlaceholder
    | FloorplanTransmitterPlaceholder
    | FloorplanGasWarningCenterPlaceholder
    | FloorplanFlameDetectorPlaceholder;

  readonly extent = input.required<FloorplanSidesheetExtent>();
  readonly toggleExtendedButtonClicked = output<void>();

  protected readonly FloorplanTransmitterPlaceholder = FloorplanTransmitterPlaceholder;
  protected readonly FloorplanFlameDetectorPlaceholder = FloorplanFlameDetectorPlaceholder;
  protected readonly SidesheetExtent = FloorplanSidesheetExtent;

  @Input({
    transform: (
      value: FloorplanItem,
    ):
      | FloorplanSimplePlaceholder
      | FloorplanTransmitterPlaceholder
      | FloorplanGasWarningCenterPlaceholder
      | FloorplanFlameDetectorPlaceholder => {
      if (value instanceof FloorplanSimplePlaceholder) {
        return value as FloorplanSimplePlaceholder;
      } else if (value instanceof FloorplanTransmitterPlaceholder) {
        return value as FloorplanTransmitterPlaceholder;
      } else if (value instanceof FloorplanGasWarningCenterPlaceholder) {
        return value as FloorplanGasWarningCenterPlaceholder;
      } else if (value instanceof FloorplanFlameDetectorPlaceholder) {
        return value as FloorplanFlameDetectorPlaceholder;
      } else {
        throw new Error("Invalid FloorplanItem type");
      }
    },
    required: true,
  })
  set floorplanItem(
    value:
      | FloorplanSimplePlaceholder
      | FloorplanTransmitterPlaceholder
      | FloorplanGasWarningCenterPlaceholder
      | FloorplanFlameDetectorPlaceholder,
  ) {
    this._floorplanItem = value;
  }

  get floorplanItem():
    | FloorplanSimplePlaceholder
    | FloorplanTransmitterPlaceholder
    | FloorplanGasWarningCenterPlaceholder
    | FloorplanFlameDetectorPlaceholder {
    return this._floorplanItem;
  }

  constructor() {
    this.isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
    this.hasWritePermission$ = this.projectService.hasUserWritePermission$.pipe(
      filter((val): val is boolean => val !== undefined),
    );
  }

  ngOnInit(): void {
    this.setPlaceholderData();
  }

  protected isTransmitterPlaceholder(floorplanItem: FloorplanItem) {
    return floorplanItem instanceof FloorplanTransmitterPlaceholder;
  }

  protected getTransmitterPlaceholder(
    floorplanItem: FloorplanSimplePlaceholder | FloorplanTransmitterPlaceholder | FloorplanGasWarningCenterPlaceholder,
  ) {
    return floorplanItem as FloorplanTransmitterPlaceholder;
  }

  protected isGasWarningCenterPlaceholder(floorplanItem: FloorplanItem) {
    return floorplanItem instanceof FloorplanGasWarningCenterPlaceholder;
  }

  protected getGasWarningCenterPlaceholder(
    floorplanItem: FloorplanSimplePlaceholder | FloorplanGasWarningCenterPlaceholder | FloorplanTransmitterPlaceholder,
  ) {
    return floorplanItem as FloorplanGasWarningCenterPlaceholder;
  }

  protected isFlameDetectorPlaceholder(floorplanItem: FloorplanItem) {
    return floorplanItem instanceof FloorplanFlameDetectorPlaceholder;
  }

  protected getFlameDetectorPlaceholder(
    floorplanItem:
      | FloorplanSimplePlaceholder
      | FloorplanGasWarningCenterPlaceholder
      | FloorplanTransmitterPlaceholder
      | FloorplanFlameDetectorPlaceholder,
  ) {
    return floorplanItem as FloorplanFlameDetectorPlaceholder;
  }

  private setPlaceholderData() {
    if (this.floorplanItem instanceof FloorplanTransmitterPlaceholder) {
      this.imageUrl = "./assets/icons-blue/transmitter_platzhalter_blau.svg";
      return;
    } else if (this.floorplanItem instanceof FloorplanGasWarningCenterPlaceholder) {
      this.imageUrl = "./assets/icons-blue/gaswarnzentrale_platzhalter_blau.svg";
      return;
    } else if (this.floorplanItem instanceof FloorplanFlameDetectorPlaceholder) {
      this.imageUrl = "./assets/icons-blue/brandmelder_platzhalter_blau.svg";
      return;
    }
    switch ((this.floorplanItem as FloorplanSimplePlaceholder).type) {
      case "alarmDevice":
        this.imageUrl = "./assets/icons-blue/alarmmittel_platzhalter_blau.svg";
        break;
      case "plasticSign":
        this.imageUrl = "./assets/icons-blue/kunststoffschild_platzhalter_blau.svg";
        break;
      case "signalElement":
        this.imageUrl = "./assets/icons-blue/leuchttransparent_platzhalter_blau.svg";
    }
  }
}
